import type { ClientOrgProviders, ProviderSetup, ProviderSetupStep } from '@/api/schemasMembers';
import type { PlatformProviders } from '@/api/schemasPlatformProviders';

/**
 * How the Clients page words a client's provider setup (SPEC decision 19): Hangar creates the client's Expo organization
 * and registers Malible's Bitrise workspace for it. Pure, so the copy is unit-tested.
 */
export type ProviderStepKind = keyof ProviderSetup;

export const PROVIDER_LABEL: Record<ProviderStepKind, string> = { eas: 'Expo', bitrise: 'Bitrise' };
const ACCOUNT_NOUN: Record<ProviderStepKind, string> = { eas: 'Expo organization', bitrise: 'Bitrise workspace' };

export interface StepView {
  /** A status color token, always shown with the icon and the text. */
  tone: 'succeeded' | 'awaiting' | 'failed';
  /** A Material Symbols name from design/DESIGN.md. */
  icon: 'check_circle' | 'warning' | 'error';
  text: string;
}

/** One step's result, worded for the operator: an icon and a label, never color alone. */
export function stepView(kind: ProviderStepKind, step: ProviderSetupStep): StepView {
  const account = step.account ? ` ${step.account}` : '';
  switch (step.status) {
    case 'created':
      return { tone: 'succeeded', icon: 'check_circle', text: kind === 'eas' ? `${ACCOUNT_NOUN.eas}${account} created` : `${ACCOUNT_NOUN.bitrise}${account} registered` };
    case 'ready':
      return { tone: 'succeeded', icon: 'check_circle', text: `Already set up:${account || ' yes'}` };
    case 'not_configured':
      return { tone: 'awaiting', icon: 'warning', text: `Not configured${step.message ? `: ${step.message}` : ''}` };
    default:
      return { tone: 'failed', icon: 'error', text: `Failed${step.message ? `: ${step.message}` : ''}` };
  }
}

/** True when the client still lacks its Expo or Bitrise account (the "Set up providers" action is offered). */
export const needsSetup = (p: ClientOrgProviders): boolean => !p.eas || !p.bitrise;

export interface SetupGate {
  /** Set when the action cannot do anything: neither of Malible's tokens is set. */
  blockedReason?: string;
  /** What the action does with the tokens there are (the tooltip while it is allowed). */
  hint?: string;
}

const SETUP_HINT = 'Creates the client\'s Expo organization and registers Malible\'s Bitrise workspace for it';

/**
 * Whether "Set up providers" can do anything, from Malible's own tokens (Hangar administration → Expo & Bitrise).
 * Unknown (still loading, or the read failed) never blocks: the API answers `not_configured` per step anyway.
 */
export function setupGate(page: Pick<PlatformProviders, 'expo' | 'bitrise'> | undefined): SetupGate {
  if (!page) return { hint: SETUP_HINT };
  const expo = page.expo.configured;
  const bitrise = page.bitrise.configured;
  if (!expo && !bitrise) return { blockedReason: 'Set Malible\'s Expo and Bitrise tokens first (Expo & Bitrise)' };
  if (!bitrise) return { hint: 'Malible\'s Bitrise token is not set: only the Expo organization is created' };
  if (!expo) return { hint: 'Malible\'s Expo token is not set: only the Bitrise workspace is registered' };
  return { hint: SETUP_HINT };
}

/** True when every step left the client with its account. */
export const setupSucceeded = (setup: ProviderSetup): boolean => [setup.eas, setup.bitrise].every((s) => s.status === 'created' || s.status === 'ready');

/** True when a step did not run because Malible's own Expo or Bitrise token is not set (Hangar administration → Expo & Bitrise). */
export const providersNotConfigured = (setup: ProviderSetup): boolean => [setup.eas, setup.bitrise].some((s) => s.status === 'not_configured');

/** One line for a toast: "Expo: created · Bitrise: not configured". */
export function setupSummary(setup: ProviderSetup): string {
  const word = (s: ProviderSetupStep): string => s.status.replace('_', ' ');
  return `${PROVIDER_LABEL.eas}: ${word(setup.eas)} · ${PROVIDER_LABEL.bitrise}: ${word(setup.bitrise)}`;
}
